import React from "react";
import { HomeIcon, Moon, Building2, Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader, FinalCTA } from "@/components/PageHeader";

const services = [
  {
    icon: HomeIcon,
    title: "Supportive In-Home Services",
    body:
      "Person-centered support provided in the individual's home and community, focused on building skills, increasing independence, supporting daily routines, and working toward goals identified in the individual's service plan.",
    bullets: [
      "Skill development",
      "Daily routines",
      "Community participation",
      "Independence-building",
      "ISP-aligned support",
    ],
    testId: "services-inhome",
  },
  {
    icon: Moon,
    title: "Personal Care Assistance",
    subtitle: "Overnight Support",
    body:
      "Individualized overnight assistance for individuals who require support during nighttime hours, based on their needs and authorized services.",
    bullets: [
      "Overnight support",
      "Personal care assistance",
      "Nighttime routines",
      "Safety and well-being",
      "Individualized support",
    ],
    testId: "services-pca",
  },
  {
    icon: Building2,
    title: "Residential Supported Living",
    body:
      "Individualized residential support designed to help individuals develop and maintain the skills needed to live as safely and independently as possible in their home and community.",
    bullets: [
      "Daily living support",
      "Independent living skills",
      "Household routines",
      "Community participation",
      "Person-centered support",
    ],
    testId: "services-residential",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader
        eyebrow="Our Services"
        title="Support designed around"
        accent="you"
        kicker="."
        subtitle="WeCare II provides individualized support designed around each person's needs, goals, preferences, and authorized service plan — helping individuals live safely, confidently, and as independently as possible."
        data-testid="services-header"
      />

      {/* Three core services */}
      <section className="wc-container -mt-6 md:-mt-8 pb-12 md:pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {services.map(({ icon: Icon, title, subtitle, body, bullets, testId }) => (
            <div
              key={title}
              className="bg-brand-navy/[0.04] backdrop-blur-sm rounded-3xl border border-brand-navy/15 p-8 flex flex-col hover:bg-brand-navy/[0.06] hover:border-brand-navy/25 hover:shadow-[0_20px_40px_rgba(30,42,120,0.12)]"
              style={{ transition: "background-color 300ms ease, border-color 300ms ease, box-shadow 300ms ease" }}
              data-testid={testId}
            >
              <div className="w-12 h-12 rounded-2xl bg-brand-navy text-white grid place-items-center">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="mt-6 font-outfit text-xl font-semibold text-brand-navy tracking-tight leading-snug">
                {title}
              </h3>
              {subtitle && (
                <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-orange">
                  {subtitle}
                </div>
              )}
              <p className="mt-3 text-slate-600 leading-relaxed text-sm">{body}</p>
              <ul className="mt-6 space-y-2">
                {bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-brand-orange shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="mt-6 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-brand-orange hover:brightness-90 self-start"
                data-testid={`${testId}-cta`}
              >
                Request this service <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Who We Support — compact */}
      <section className="wc-container pb-16 md:pb-20" data-testid="services-who-we-support">
        <div className="rounded-3xl bg-brand-bone/70 border border-brand-stone/60 p-8 md:p-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-navy">
              <span className="h-px w-8 bg-brand-orange" />
              Who We Support
            </div>
            <h2 className="mt-4 font-outfit text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy leading-[1.1]">
              Individualized support for{" "}
              <span
                className="italic font-medium text-brand-orange relative whitespace-nowrap"
                style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
              >
                every stage of life
                <svg
                  className="absolute -bottom-1.5 left-0 w-full"
                  viewBox="0 0 220 12"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path
                    d="M 4 8 C 60 2, 130 12, 216 4"
                    stroke="#f39325"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className="text-brand-navy">.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-[15px] md:text-base leading-relaxed max-w-2xl">
              WeCare II provides person-centered services to children and adults with intellectual and developmental
              disabilities. Support is individualized according to each person's needs, goals, preferences, living
              situation, and authorized service plan.
            </p>
            <div
              className="mt-6 inline-flex items-start gap-2 text-[13px] text-brand-navy font-semibold leading-relaxed"
              data-testid="services-service-area"
            >
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0" />
              <span>
                Proudly serving Virginia Beach, Chesapeake, Norfolk, Suffolk, Portsmouth, Hampton, Newport News, and the
                Eastern Shore.
              </span>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA
        title="Ready to build a support plan?"
        subtitle="Whether you're seeking services for yourself, a loved one, or an individual you support, our team is here to help you understand your options and take the next step."
        primaryLabel="Request Services"
        primaryTo="/contact"
        secondaryLabel="Make a Referral"
        secondaryTo="/refer"
      />
    </div>
  );
}
